import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useAuth } from "../../../providers/authprovider";

const COLUMN_NAME = "departments";
const CODE_PATTERN = /^[\p{L}]{2,3}$/u;

function parseColumnValue(rawValue) {
  let parsed = rawValue;

  if (typeof rawValue === "string") {
    try {
      parsed = JSON.parse(rawValue);
    } catch (e) {
      return [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      code: (item.code ?? "").toString(),
      name: (item.name ?? "").toString(),
    }));
}

const EMPTY_DEPARTMENT = { code: "", name: "" };

// Generuje propozycje skrotu z nazwy (inicjaly slow / poczatek slowa),
// pomijajac skroty juz zajete przez inne wydzialy.
function generateCode(name, existingCodesUpper) {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.replace(/[^\p{L}]/gu, ""))
    .filter(Boolean);

  if (words.length === 0) return "";

  const candidates = [];

  if (words.length >= 3) candidates.push(words.slice(0, 3).map((w) => w[0]).join(""));
  if (words.length >= 2) {
    candidates.push(words.slice(0, 2).map((w) => w[0]).join(""));
    candidates.push(words[0].slice(0, 2) + words[1][0]);
    candidates.push(words[0][0] + words[1].slice(0, 2));
  }
  candidates.push(words[0].slice(0, 3));
  candidates.push(words[0].slice(0, 2));

  const normalized = [...new Set(candidates.map((c) => c.toUpperCase()))].filter((c) =>
    CODE_PATTERN.test(c),
  );

  const free = normalized.find((c) => !existingCodesUpper.has(c));
  if (free) return free;

  const base = (normalized[0] || words[0].slice(0, 2).toUpperCase()).slice(0, -1);
  for (let i = 0; i < 26; i += 1) {
    const candidate = (base + String.fromCharCode(65 + i)).slice(-3);
    if (CODE_PATTERN.test(candidate) && !existingCodesUpper.has(candidate)) return candidate;
  }

  return normalized[0] || "";
}

function DepartmentEditModal({ show, department, departments, onHide, onSave }) {
  const [formData, setFormData] = useState(EMPTY_DEPARTMENT);
  const [codeTouched, setCodeTouched] = useState(false);
  const [error, setError] = useState("");
  const isEdit = !!department;

  const existingCodesUpper = useMemo(
    () =>
      new Set(
        departments
          .filter((item) => !isEdit || item.code.toUpperCase() !== department.code.toUpperCase())
          .map((item) => item.code.toUpperCase()),
      ),
    [departments, isEdit, department],
  );

  useEffect(() => {
    if (!show) return;

    setFormData(department ? { ...department } : EMPTY_DEPARTMENT);
    setCodeTouched(isEdit);
    setError("");
  }, [show, department, isEdit]);

  const handleNameChange = (event) => {
    const name = event.target.value;

    setFormData((prev) => ({
      ...prev,
      name,
      code: codeTouched ? prev.code : generateCode(name, existingCodesUpper),
    }));
  };

  const handleCodeChange = (event) => {
    setCodeTouched(true);
    setFormData((prev) => ({ ...prev, code: event.target.value.toUpperCase() }));
  };

  const handleSave = () => {
    const code = formData.code.trim().toUpperCase();
    const name = formData.name.trim();

    if (!CODE_PATTERN.test(code)) {
      setError("Skrót musi mieć 2-3 litery.");
      return;
    }

    if (!name) {
      setError("Wpisz nazwę wydziału.");
      return;
    }

    const duplicate = departments.some(
      (item) =>
        item.code.toUpperCase() === code &&
        (!isEdit || item.code.toUpperCase() !== department.code.toUpperCase()),
    );

    if (duplicate) {
      setError("Wydział o takim skrócie już istnieje.");
      return;
    }

    setError("");
    onSave({ code, name }, department);
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edytuj wydział" : "Dodaj wydział"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form>
          <Form.Group className="mb-3" controlId="department_name">
            <Form.Label>Nazwa wydziału</Form.Label>
            <Form.Control
              value={formData.name}
              type="text"
              name="name"
              onChange={handleNameChange}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="department_code">
            <Form.Label>Skrót (2-3 litery)</Form.Label>
            <Form.Control
              value={formData.code}
              type="text"
              name="code"
              maxLength={3}
              onChange={handleCodeChange}
              style={{ textTransform: "uppercase" }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Anuluj
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Zapisz
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function DepartmentDeleteModal({ show, department, onHide, onConfirm }) {
  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Usuń wydział</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div>Czy na pewno chcesz usunąć wydział?</div>
        <div className="text-muted small mt-2">
          {department?.code} - {department?.name}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Anuluj
        </Button>
        <Button variant="danger" onClick={onConfirm}>
          Usuń
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function DepartmentsSettingsTable({ settingsData, onSettingsSaved }) {
  const { settings } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editModal, setEditModal] = useState({ show: false, department: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, department: null });

  const departments = useMemo(
    () => parseColumnValue(settingsData?.[COLUMN_NAME]),
    [settingsData],
  );

  const saveDepartments = async (nextDepartments) => {
    setSaving(true);
    setError("");

    const hasColumn = settingsData?.[COLUMN_NAME] !== undefined;
    const result = hasColumn
      ? await settings.editSetting({ column: COLUMN_NAME, json: nextDepartments })
      : await settings.saveSetting({ column: COLUMN_NAME, json: nextDepartments });

    setSaving(false);

    if (!result?.success) {
      setError(result?.error || "Nie udało się zapisać wydziałów.");
      return;
    }

    onSettingsSaved?.(result.content || {});
  };

  const handleSave = (department, originalDepartment) => {
    let next;

    if (originalDepartment) {
      next = departments.map((item) =>
        item.code === originalDepartment.code ? department : item,
      );
    } else {
      next = [...departments, department];
    }

    setEditModal({ show: false, department: null });
    saveDepartments(next);
  };

  const handleDelete = () => {
    const next = departments.filter(
      (item) => item.code !== deleteModal.department.code,
    );

    setDeleteModal({ show: false, department: null });
    saveDepartments(next);
  };

  return (
    <div className="p-3 border rounded bg-light mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Wydziały</h5>
        <div className="d-flex align-items-center gap-2">
          {saving && <Spinner size="sm" />}
          <Button
            size="sm"
            onClick={() => setEditModal({ show: true, department: null })}
          >
            Dodaj wydział
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Table size="sm" striped bordered hover responsive className="mb-0 align-middle">
        <thead>
          <tr>
            <th>#</th>
            <th>Skrót</th>
            <th>Nazwa</th>
            <th>Akcje</th>
          </tr>
        </thead>
        <tbody>
          {departments.length === 0 && (
            <tr>
              <td colSpan={4} className="text-muted text-center">
                Brak wydziałów.
              </td>
            </tr>
          )}
          {departments.map((department, index) => (
            <tr key={department.code}>
              <td>{index + 1}</td>
              <td>{department.code}</td>
              <td>{department.name}</td>
              <td className="text-nowrap">
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="me-2"
                  onClick={() => setEditModal({ show: true, department })}
                >
                  Edytuj
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => setDeleteModal({ show: true, department })}
                >
                  Usuń
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {editModal.show && (
        <DepartmentEditModal
          show={editModal.show}
          department={editModal.department}
          departments={departments}
          onHide={() => setEditModal({ show: false, department: null })}
          onSave={handleSave}
        />
      )}

      {deleteModal.show && (
        <DepartmentDeleteModal
          show={deleteModal.show}
          department={deleteModal.department}
          onHide={() => setDeleteModal({ show: false, department: null })}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
